import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import SearchableDropdown from '../../common/SearchableDropdown';
import { Refresh, GetApp, ArrowBack, Print } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY     = '#263078';
const PRIMARY_MID = '#3949ab';
const PRIMARY_LT  = '#e8eaf6';

const fmt = (n) =>
    `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt2Date = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const thBase = { padding: '7px 10px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', color: '#fff', background: PRIMARY_MID, border: `1px solid ${PRIMARY_MID}` };
const tdBase = { padding: '6px 10px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };
const tdR    = { ...tdBase, textAlign: 'right' };

const BankBookReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [banks, setBanks]       = useState([]);
    const [bankId, setBankId]     = useState('');
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    // Load bank list
    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('BankApi', `/${companyId}`, token)
            .then(res => {
                const list = res.response_data || res || [];
                setBanks(Array.isArray(list) ? list : []);
            })
            .catch(() => {});
    }, [companyId, token]);
    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        if (!bankId)              { setError('Please select a bank');      return; }
        setLoading(true); setError('');
        try {
            const res = await fetchData(
                'FinancialStatementsReportApi',
                `/BankBookReport/${companyId}/${branchId}/${bankId}/${fromDate}/${toDate}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setData([]);
            setFetched(true);
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.Particular  || '').toLowerCase().includes(q) ||
            (r.BillNumber  || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => ({
        debit:  filtered.reduce((s, r) => s + (parseFloat(r.Debit)  || 0), 0),
        credit: filtered.reduce((s, r) => s + (parseFloat(r.Credit) || 0), 0),
    }), [filtered]);

    const selectedBankName = banks.find(b => String(b.MasterId) === String(bankId))?.Name || '';

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'BankBookReport', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Date', 'Bill No', 'Particular', 'Debit (₹)', 'Credit (₹)', 'Balance (₹)'];
        const rows = filtered.map((r, i) => [
            i + 1,
            fmtDate(r.BillDate),
            r.BillNumber  || '',
            r.Particular  || '',
            parseFloat(r.Debit)           || 0,
            parseFloat(r.Credit)          || 0,
            parseFloat(r.RunningBalance)  || 0,
        ]);

        await exportStyledExcel({
            reportTitle: 'BANK BOOK REPORT',
            sheetName: 'Bank Book',
            fileName: `BankBook_${selectedBankName}_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', 'Total Credit', '', totals.credit, ''],
            columnWidths: [4, 14, 14, 36, 16, 16, 18],
        });
    };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading bank book..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Bank Book
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Financial Statements</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />

                    {/* Bank selector */}
                    <div style={{ flexShrink: 0, minWidth: 180 }}>
                        <SearchableDropdown
                            options={banks.map(b => ({ value: String(b.MasterId), label: b.Name || '' }))}
                            value={bankId}
                            onChange={val => setBankId(val)}
                            placeholder="Select Bank"
                            minWidth={180}
                            primaryColor={PRIMARY}
                        />
                    </div>

                    {/* Search */}
                    <input
                        placeholder="Search particular / bill..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 160, outline: 'none' }}
                    />

                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />

                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Print" arrow>
                        <IconButton onClick={() => window.print()} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <Print sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', alignItems: 'center' }}>
                        {[
                            { label: 'Entries',       value: filtered.length,       color: PRIMARY },
                            { label: 'Total Debit',   value: fmt(totals.debit),     color: '#c62828' },
                            { label: 'Total Credit',  value: fmt(totals.credit),    color: '#2e7d32' },
                            { label: 'Net Balance',   value: fmt(totals.credit - totals.debit), color: (totals.credit - totals.debit) >= 0 ? '#2e7d32' : '#c62828' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                        {selectedBankName && (
                            <span style={{ fontSize: 11, color: PRIMARY, fontWeight: 600, marginLeft: 8 }}>
                                🏦 {selectedBankName}
                            </span>
                        )}
                        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                            {fromDate} to {toDate}
                            {search && ` | filtered from ${data.length}`}
                        </span>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 180px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)` }}>
                                <th style={{ ...thBase, textAlign: 'center', width: 40 }}>#</th>
                                <th style={{ ...thBase, minWidth: 110 }}>Date</th>
                                <th style={{ ...thBase, minWidth: 120 }}>Bill No</th>
                                <th style={{ ...thBase, minWidth: 260 }}>Particular</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 130 }}>Debit (₹)</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 130 }}>Credit (₹)</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 140, background: '#1a237e' }}>Balance (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
                                        No records found
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const debit   = parseFloat(r.Debit)          || 0;
                                const credit  = parseFloat(r.Credit)         || 0;
                                const balance = parseFloat(r.RunningBalance) || 0;
                                const bg = i % 2 === 0 ? '#fff' : PRIMARY_LT;
                                return (
                                    <tr key={i} style={{ background: bg }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, color: '#64748b' }}>{fmtDate(r.BillDate)}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{r.BillNumber || '-'}</td>
                                        <td style={{ ...tdBase, color: '#1e293b' }}>{r.Particular || '-'}</td>
                                        <td style={{ ...tdR, color: debit  ? '#c62828' : '#cbd5e1', fontWeight: debit  ? 600 : 400 }}>
                                            {debit  ? fmt(debit)  : '-'}
                                        </td>
                                        <td style={{ ...tdR, color: credit ? '#2e7d32' : '#cbd5e1', fontWeight: credit ? 600 : 400 }}>
                                            {credit ? fmt(credit) : '-'}
                                        </td>
                                        <td style={{ ...tdR, fontWeight: 700, color: balance >= 0 ? '#2e7d32' : '#c62828' }}>
                                            {fmt(Math.abs(balance))} {balance >= 0 ? 'Cr' : 'Dr'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={4} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.debit)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(totals.credit)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: (totals.credit - totals.debit) >= 0 ? '#2e7d32' : '#c62828' }}>
                                        {fmt(Math.abs(totals.credit - totals.debit))} {(totals.credit - totals.debit) >= 0 ? 'Cr' : 'Dr'}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default BankBookReport;
